/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  FIND_FEATURE_ASSIGNABLE_WORKSPACES,
  SortDirectionEnum,
} from '@gripp/shared-logic';
import { BaseColumnCheckbox, Colors, InfiniteDataGrid } from '@gripp/shared-ui';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, StyleProp, StyleSheet, Text } from 'react-native';

export type FeatureWorkspaceAssignmentListProps = {
  featureId: string;
  maxHeight?: number;
  keyword?: string;
  workspaceAssignments: string[];
  onWorkspaceAssignmentsChange: (assignments: string[]) => void;
};

export const FeatureWorkspaceAssignmentList = ({
  featureId,
  maxHeight,
  keyword,
  workspaceAssignments,
  onWorkspaceAssignmentsChange,
}: FeatureWorkspaceAssignmentListProps) => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<any>(() => {
    if (featureId) {
      return {
        keyword,
        featureId,
        assignments: workspaceAssignments,
      };
    }
    return undefined;
  });

  useEffect(() => {
    setFilter({
      keyword,
      featureId,
      assignments: workspaceAssignments,
    });
  }, [keyword, featureId]);

  const columnHelper = createColumnHelper<any>();

  const getLabel = (labelStyle: StyleProp<any>, label: string) => {
    return <Text style={[labelStyle, styles.cellText]}>{label}</Text>;
  };

  const columns = [
    columnHelper.accessor('selected', {
      cell: BaseColumnCheckbox.checkbox,
      header: BaseColumnCheckbox.checkboxHeader,
      meta: { style: styles.checkboxCell },
    }),
    columnHelper.accessor('name', {
      cell: (info) => getLabel(styles.nameLabel, info.getValue()),
      header: () => t('features.assignment.name'),
      meta: { style: styles.cell },
    }),
  ] as ColumnDef<any, any>[];

  if (!filter) return null;

  return (
    <InfiniteDataGrid
      modelName="workspace"
      hideResultsLabel={true}
      query={FIND_FEATURE_ASSIGNABLE_WORKSPACES}
      filter={filter}
      pageSize={100}
      columns={columns}
      listMaxHeight={maxHeight as number}
      containerStyle={{ height: maxHeight }}
      paddingBottom={0}
      textEmpty={t('features.assignment.noWorkspaces')}
      dataPrefix="feature.findAssignableWorkspaces"
      orderBy={{ field: 'name', order: SortDirectionEnum.Asc }}
      updateRowsSelected={(rows) => {
        onWorkspaceAssignmentsChange(rows.map((row) => row.id));
      }}
      isRowSelected={(row) => {
        return workspaceAssignments.includes(row.id);
      }}
      enableScrollRefresh={true}
      showScrollIndicator={true}
    />
  );
};

const styles = StyleSheet.create({
  cell: {
    alignItems: 'center',
    justifyContent: 'flex-start',
    overflow: 'hidden',
    paddingVertical: 4,
    ...Platform.select({
      web: {
        height: 50,
        paddingRight: 16,
      },
      default: {
        height: 68,
        paddingRight: 12,
      },
    }),
  },
  checkboxCell: {
    maxWidth: 50,
    flexDirection: 'row',
    justifyContent: 'center',
  },
  nameLabel: {
    color: Colors.black,
  },
  cellText: {
    fontSize: 16,
    lineHeight: 17,
    ...Platform.select({
      web: {
        fontWeight: '500',
        flexWrap: 'nowrap',
      },
      default: {
        fontWeight: '400',
        flexWrap: 'wrap',
      },
    }),
  },
});
